/* global $:false */
import { dateHourFormatLocale, datetimeFormatForLocale } from '@cob/ui-genesis/helpers/cob.translator.js';
import { getStartOfWeek } from './recordm.fields.date.js';
import utils from '@cob/ui-genesis/cob.utils.js';

//Returns the given timestamp formatted in the passed locale
var format = function(timestamp, locale) {
    return utils.time.getLocalDateTimeFromTimeStamp(parseInt(timestamp, 10), locale);
};

var getTimestampFor = function(dateExpression){

    if(dateExpression === "now"){
        return new Date().getTime();
    }

    return null;
};

// args:
// * `confs`: the field configuration
//
// confs:
// * `id`: the input attr id
// * `cssClasses`: A string space separated with a list of css classes to add to the input
// * `readonly`: true or false. Defaults to false
// * `valueChanged(newValue)`: Invoked when a user changes the value
//
var DateTime = function(core, confs) {

    var _$input,
        _lastValueInMilliSec;

    const _datePickerConfs = {};

    var _confs = {
        id: core.locallyUniqueId(),
        fieldDefinition: {name: ""},
        cssClasses: undefined,
        readonly: false,
        valueChanged: undefined,

        format: datetimeFormatForLocale(core.getLocale(), core.getLanguage()),
        formatTime: dateHourFormatLocale(core.getLocale(), core.getLanguage()), // configure the hour selector format
        lang: core.getLanguage(),

        dayOfWeekStart: getStartOfWeek(core.getLanguage(), core.getLocale())
    };

    utils.extend(_confs, confs, true);

    var _setValue = function(valueInMilliSec) {
        if (_lastValueInMilliSec !== valueInMilliSec) {
            _lastValueInMilliSec = valueInMilliSec;

            if (_confs.valueChanged) {
                _confs.valueChanged();
            }
        }
    };

    var _onValueChange = function(newDate) {
        if (newDate) {
            const ours = format(newDate.valueOf(), core.getLocale());
            const pick = _$input.val();

            // Aparently they have different space characters ???
            if(ours.replace(/\s+/g, "") === pick.replace(/\s+/g, "")){
                const timestamp = newDate.valueOf()
                _setValue(timestamp);
                _$input.attr('raw-value', timestamp);

            } else {
                // this means that the user entered an invalid value (probably)
                // we reset to the previous one, or to the clear state
                const previousTs = _$input.attr('raw-value');
                if(!previousTs || previousTs === ''){
                    _$input.val('');
                    _setValue(previousTs);

                } else {
                    _$input.val(format(previousTs, core.getLocale()));
                    _setValue(previousTs);
                }
                const seemsTheSame = (ours.replaceAll(/[ ,0]/g, '') === pick.replaceAll(/[ ,0]/g, ''));
                if(seemsTheSame){
                    const msg = `Suspicious diff on date formatting, check cob.translator.js formats
                    {{locale:${core.getLocale()}, ours:[${ours}], picker:[${pick}]}}`;
                    window.console.warn(msg);
                    if(typeof Sentry !== 'undefined') Sentry.captureMessage(msg);
                }
            }

        } else {
            _setValue(undefined);
            _$input.attr('raw-value', "");
        }
    };

    var _render = function() {
        var $html = $([
            '<div class="inline">',
            '<input id="f' + _confs.id + '" class="field-value cob-field-value datetime-field" type="text" maxlength=19 placeholder="' + _confs.fieldDefinition.name + '">',
            '</div>'
        ].join(''));

        utils.extend(_datePickerConfs, _confs, true);

        //We have to remove the "id" field, so that there are not 2 DOM elements
        // with the same id: the date input and the date picker
        _datePickerConfs.id = undefined;

        _$input = $html.find("input");

        if (_datePickerConfs.cssClasses) {
            _$input.addClass(_datePickerConfs.cssClasses);
        }

        if (_datePickerConfs.readonly) {
            _$input.attr("readonly", "readonly")
                .attr("tabindex", -1);

        } else {
            _datePickerConfs.onChangeDateTime = _onValueChange;
            _$input = _$input.datetimepicker(_datePickerConfs);
        }

        if (_lastValueInMilliSec) {
            _$input.val(format(_lastValueInMilliSec, core.getLocale()));
            _$input.attr('raw-value', _lastValueInMilliSec);
        }

        return $html;
    };

    var _clearError = function() {
        _$input.removeClass("error");
    };

    var _showError = function() {
        _$input.addClass("error");
    };

    var _destroy = function() {
        if (!_confs.readonly) {
            _$input.datetimepicker('destroy');
        }
    };

    return {
        render: _render,
        destroy: _destroy,

        clearError: _clearError,
        showError: _showError,

        setValue: function(value) {
            if (!value) return;

            if (_$input) {
                _$input.val(format(value, core.getLocale()));
                _$input.attr('raw-value', value);
            }

            // Notify that this field has a new value because
            // I want to update the $auto.join and other fields...
            _setValue(value);
        },
        getValue: function() {
            return _lastValueInMilliSec ? String(_lastValueInMilliSec) : undefined;
        },

        disableSelf: function() {
            _$input.prop("readonly", true).attr("tabindex", -1);
            _$input.datetimepicker('destroy');
        },
        enableSelf: function() {
            _$input.removeProp("readonly").removeAttr("tabindex");
            _datePickerConfs.onChangeDateTime = _onValueChange;
            _$input.datetimepicker(_datePickerConfs);
        },
        isEnabled: function () {
            return !_$input.prop('readonly');
        }
    }
};

export {  getTimestampFor, format, DateTime };
